import { DependencyList, useState } from 'react';
import { isFunction, isUndefined } from 'lodash-es';
import { useMemoizedFn, useUpdateEffect } from 'ahooks';
import { isBrowser } from '@cap/ssr-common-utils';
import { logger } from '../utils/logger';

export type SetState<S> = S | ((prevState?: S) => S);

export type IFuncUpdater<T> = (previousState?: T) => T;

export interface StorageOptions<T> {
  /**
   * 序列化函数，默认采用 JSON.stringify 序列化
   * @param value
   * @returns
   */
  serializer?: (value: T) => string;
  /**
   * 反序列化函数，默认采用 JSON.parse 反序列化
   * @param value
   * @returns
   */
  deserializer?: (value: string) => T;
  /**
   * 默认值
   */
  defaultValue?: T | IFuncUpdater<T>;
  /**
   * 出错的回调函数
   * @param error
   * @returns
   */
  onError?: (error: unknown) => void;
}

export function createUseStorageState(getStorage: () => Storage | undefined) {
  /**
   *
   * @param key 存储Key
   * @param deps 依赖变化
   * @param options 可选项
   * @returns
   */
  function useStorageState<T>(
    key: string,
    deps: DependencyList = [],
    options: StorageOptions<T> = {},
  ) {
    let storage: Storage | undefined;
    const {
      onError = (e) => {
        logger.error(e as Error);
      },
    } = options;

    // https://github.com/alibaba/hooks/issues/800
    try {
      storage = getStorage();
    } catch (err) {
      onError(err);
    }

    const serializer = (value: T) => {
      if (options.serializer) {
        return options.serializer(value);
      }
      return JSON.stringify(value);
    };

    const deserializer = (value: string): T => {
      if (options.deserializer) {
        return options.deserializer(value);
      }
      return JSON.parse(value);
    };

    function getStoredValue() {
      try {
        const raw = storage?.getItem(key);
        if (raw) {
          return deserializer(raw);
        }
      } catch (e) {
        onError(e);
      }
      if (isFunction(options.defaultValue)) {
        return options.defaultValue();
      }
      return options.defaultValue;
    }

    const [state, setState] = useState(getStoredValue);

    useUpdateEffect(() => {
      setState(getStoredValue());
    }, deps);

    const updateState = (value?: SetState<T>) => {
      const currentState = isFunction(value) ? value(state) : value;
      setState(currentState);

      if (isUndefined(currentState)) {
        storage?.removeItem(key);
      } else {
        try {
          storage?.setItem(key, serializer(currentState));
        } catch (e) {
          console.error(e);
        }
      }
    };

    return [state, useMemoizedFn(updateState)] as const;
  }
  return useStorageState;
}

/**
 * 使用 localStorage 存在状态
 */
export const useLocalStorageState = createUseStorageState(() =>
  isBrowser ? window.localStorage : undefined,
);

/**
 * 使用 sessionStorage 存在状态
 */
export const useSessionStorageState = createUseStorageState(() =>
  isBrowser ? window.localStorage : undefined,
);
